import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextField,
  Box,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  Collapse,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Mail,
  Search,
  Close,
  PersonAdd,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { db, functions } from '../../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

interface User {
  id: string; // Changed from number to string to use Firestore doc IDs
  name: string;
  email: string;
  role: 'student' | 'teacher' | 'judge' | 'volunteer' | 'sponsor';
}

interface EmailPopupProps {
  open: boolean;
  onClose: () => void;
  fairName?: string;
  fairId?: string;
}

export function EmailPopup({
  open,
  onClose,
  fairName,
  fairId,
}: EmailPopupProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [error, setError] = useState('');
  const [fairUsers, setFairUsers] = useState<User[]>([]);
  const [sponsors, setSponsors] = useState<{ name: string; email: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Function to fetch all users associated with a fair
  const fetchFairUsers = async (fairId: string) => {
    setLoading(true);
    setError('');
    try {
      const userMap = new Map<string, User>();

      // 1. Get students from projects in this fair (and their sponsors)
      const projectsRef = collection(db, 'projects');
      const projectsQuery = query(projectsRef, where('fairId', '==', fairId));
      const projectsSnapshot = await getDocs(projectsQuery);

      const studentIds: string[] = [];
      const sponsorSet: Set<{
        name: string;
        email: string;
      }> = new Set();

      projectsSnapshot.forEach((doc) => {
        const projectData = doc.data();
        if (projectData.memberIds) {
          studentIds.push(...projectData.memberIds);
        }
        // Collect sponsor information
        if (projectData.adultSponsor && projectData.adultSponsor.email) {
          sponsorSet.add({
            name: projectData.adultSponsor.name,
            email: projectData.adultSponsor.email,
          });
        }
      });

      // Store sponsors separately for auto-inclusion when students are selected
      setSponsors(Array.from(sponsorSet));

      // Fetch student details
      if (studentIds.length > 0) {
        const usersRef = collection(db, 'users');
        const studentsQuery = query(
          usersRef,
          where('__name__', 'in', studentIds.slice(0, 10)) // Firestore 'in' limit is 10
        );
        const studentsSnapshot = await getDocs(studentsQuery);

        studentsSnapshot.forEach((doc) => {
          const userData = doc.data() as any;
          const user: User = {
            id: doc.id, // Use the actual Firestore document ID
            name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
            email: userData.email || '',
            role: 'student',
          };
          userMap.set(doc.id, user);
        });
      }

      // 2. Get judges assigned to this fair
      const fairDocRef = doc(db, 'fairs', fairId);
      const fairDoc = await getDoc(fairDocRef);

      if (fairDoc.exists()) {
        const fairData = fairDoc.data();
        const judgeIds =
          fairData.judges?.map((judge: any) => judge.judgeId) || [];

        if (judgeIds.length > 0) {
          const usersRef = collection(db, 'users');
          const judgesQuery = query(
            usersRef,
            where('__name__', 'in', judgeIds.slice(0, 10))
          );
          const judgesSnapshot = await getDocs(judgesQuery);

          judgesSnapshot.forEach((doc) => {
            const userData = doc.data() as any;
            const user: User = {
              id: doc.id, // Use the actual Firestore document ID
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              email: userData.email || '',
              role: 'judge',
            };
            userMap.set(doc.id, user);
          });
        }

        // 3. Get teachers and volunteers from the same region as the fair
        const regionId = fairData.regionId;
        if (regionId) {
          const usersRef = collection(db, 'users');
          const teachersAndVolunteersQuery = query(
            usersRef,
            where('regionId', '==', regionId),
            where('userType', 'in', ['teacher', 'volunteer'])
          );
          const teachersAndVolunteersSnapshot = await getDocs(
            teachersAndVolunteersQuery
          );

          teachersAndVolunteersSnapshot.forEach((doc) => {
            const userData = doc.data() as any;
            const user: User = {
              id: doc.id, // Use the actual Firestore document ID
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              email: userData.email || '',
              role: userData.userType === 'teacher' ? 'teacher' : 'volunteer',
            };
            userMap.set(doc.id, user);
          });
        }
      }

      setFairUsers(Array.from(userMap.values()));
      console.log(Array.from(userMap.values()));
    } catch (error) {
      console.error('Error fetching fair users:', error);
      setError('Failed to load users for this fair');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users when popup opens and fairId is available
  useEffect(() => {
    if (open && fairId) {
      fetchFairUsers(fairId);
    }
  }, [open, fairId]);

  // Update role checkboxes when users are manually added or removed
  useEffect(() => {
    const updatedRoles = new Set(selectedRoles);
    let rolesChanged = false;

    // Check each role type (teachers excluded from bulk selection)
    (['student', 'judge', 'volunteer'] as const).forEach((role) => {
      const allUsersOfRole = fairUsers.filter((user) => user.role === role);
      const selectedUsersOfRole = selectedUsers.filter(
        (user) => user.role === role
      );

      if (allUsersOfRole.length > 0) {
        // If all users of this role are selected, check the role
        if (
          selectedUsersOfRole.length === allUsersOfRole.length &&
          !selectedRoles.has(role)
        ) {
          updatedRoles.add(role);
          rolesChanged = true;
        }
        // If no users of this role are selected, uncheck the role
        else if (selectedUsersOfRole.length === 0 && selectedRoles.has(role)) {
          updatedRoles.delete(role);
          rolesChanged = true;
        }
      }
    });

    if (rolesChanged) {
      setSelectedRoles(updatedRoles);
    }
  }, [selectedUsers, selectedRoles, fairUsers]);

  // Filter users for manual search/add (only show users who aren't already added)
  const filteredUsers = useMemo(() => {
    return fairUsers.filter((user) => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const notAlreadyAdded = !selectedUsers.find(
        (selected) => selected.id === user.id
      );
      return matchesSearch && notAlreadyAdded;
    });
  }, [searchQuery, selectedUsers, fairUsers]);

  const handleRoleChange = (role: string, checked: boolean) => {
    const newSelectedRoles = new Set(selectedRoles);
    if (checked) {
      newSelectedRoles.add(role);
      // Add users of this role that aren't already selected
      const usersToAdd = fairUsers.filter(
        (user) =>
          user.role === role &&
          !selectedUsers.find((selected) => selected.id === user.id)
      );

      // If selecting students, also auto-add their sponsors
      if (role === 'student') {
        const sponsorsToAdd = sponsors
          .map((sponsor, index) => {
            // All sponsors are added as 'sponsor' role
            return {
              id: `sponsor_${index}_${sponsor.email}`,
              name: sponsor.name,
              email: sponsor.email,
              role: 'sponsor' as const,
            };
          })
          .filter(
            (sponsor) =>
              !selectedUsers.find((selected) => selected.id === sponsor.id)
          );

        usersToAdd.push(...sponsorsToAdd);
      }

      if (usersToAdd.length > 0) {
        setSelectedUsers((prev) => [...prev, ...usersToAdd]);
      }
    } else {
      newSelectedRoles.delete(role);
      // Remove users of this role from selected users
      setSelectedUsers((prev) => prev.filter((user) => user.role !== role));

      // If unchecking students, also remove sponsors
      if (role === 'student') {
        setSelectedUsers((prev) =>
          prev.filter((user) => {
            // Keep the user if they're not a sponsor
            return (
              !user.id.startsWith('sponsor_') &&
              !sponsors.some((sponsor) => sponsor.email === user.email)
            );
          })
        );
      }
    }
    setSelectedRoles(newSelectedRoles);
  };

  const addUser = (user: User) => {
    // Prevent multiple clicks on the same user
    if (addingUserId === user.id) {
      return;
    }

    // Check if user is already selected to prevent duplicates
    if (!selectedUsers.find((selected) => selected.id === user.id)) {
      setAddingUserId(user.id);

      setSelectedUsers((prev) => {
        // Double-check to prevent race conditions
        if (prev.find((selected) => selected.id === user.id)) {
          return prev;
        }

        const newUsers = [...prev, user];

        // If adding a student individually, also add their sponsors
        if (user.role === 'student') {
          const sponsorsToAdd = sponsors
            .map((sponsor, index) => {
              // All sponsors are added as 'sponsor' role
              return {
                id: `sponsor_${index}_${sponsor.email}`,
                name: sponsor.name,
                email: sponsor.email,
                role: 'sponsor' as const,
              };
            })
            .filter(
              (sponsor) =>
                !prev.find((selected) => selected.id === sponsor.id) &&
                sponsor.id !== user.id
            );

          newUsers.push(...sponsorsToAdd);
        }

        return newUsers;
      });

      // Clear the adding state after a short delay
      setTimeout(() => {
        setAddingUserId(null);
      }, 500);
    }
    setSearchQuery(''); // Clear search after adding
  };

  const removeUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  const getRoleChipColor = (role: string) => {
    switch (role) {
      case 'student':
        return 'primary';
      case 'teacher':
        return 'success';
      case 'judge':
        return 'secondary';
      case 'volunteer':
        return 'warning';
      case 'sponsor':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleCountStyle = (role: string) => {
    switch (role) {
      case 'student':
        return {
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
        };
      case 'teacher':
        return {
          bgcolor: 'success.light',
          color: 'success.contrastText',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
        };
      case 'judge':
        return {
          bgcolor: 'secondary.light',
          color: 'secondary.contrastText',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
        };
      case 'volunteer':
        return {
          bgcolor: 'warning.light',
          color: 'warning.contrastText',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
        };
      case 'sponsor':
        return {
          bgcolor: 'info.light',
          color: 'info.contrastText',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
        };
      default:
        return {
          bgcolor: 'grey.200',
          color: 'grey.800',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
        };
    }
  };

  const handleSendEmail = async () => {
    setError('');

    if (selectedUsers.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    if (!body.trim()) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);

    try {
      // Call the Firebase Cloud Function
      const sendBulkEmailFunc = httpsCallable(functions, 'sendBulkEmail');
      const result = await sendBulkEmailFunc({
        recipients: selectedUsers.map((u) => ({
          email: u.email,
          name: u.name,
        })),
        subject: subject || undefined, // Send undefined if empty
        body: body,
      });

      console.log('Email send result:', result.data);

      // Show success message
      setSuccessMessage(
        `Email sent successfully to ${selectedUsers.length} recipient${selectedUsers.length !== 1 ? 's' : ''}!`
      );

      // Reset form fields but keep dialog open
      setSelectedUsers([]);
      setSelectedRoles(new Set());
      setSubject('');
      setBody('');
      setShowUserSearch(false);
      setSearchQuery('');
      setAddingUserId(null);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error: any) {
      console.error('Error sending email:', error);
      setError(error.message || 'Failed to send emails. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setSelectedRoles(new Set());
    setSubject('');
    setBody('');
    setShowUserSearch(false);
    setSearchQuery('');
    setError('');
    setSuccessMessage('');
    setFairUsers([]);
    setSponsors([]);
    setLoading(false);
    setAddingUserId(null);
    onClose();
  };

  const roleCounts = {
    student: fairUsers.filter((u) => u.role === 'student').length,
    judge: fairUsers.filter((u) => u.role === 'judge').length,
    volunteer: fairUsers.filter((u) => u.role === 'volunteer').length,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Mail />
          Email {fairName && `- ${fairName}`}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ py: 4 }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading users for this fair...
            </Typography>
          </Box>
        )}

        {!loading && (
          <Box component="form" noValidate sx={{ mt: 1 }}>
            {/* Selected Recipients */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                To:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  minHeight: 60,
                  p: 2,
                  bgcolor: 'grey.50',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                {selectedUsers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recipients selected
                  </Typography>
                ) : (
                  selectedUsers.map((user) => {
                    // Check if this user is a sponsor AND if students are selected
                    const hasStudents = selectedUsers.some(
                      (u) => u.role === 'student'
                    );
                    const isSponsor =
                      hasStudents &&
                      (user.id.startsWith('sponsor_') ||
                        sponsors.some(
                          (sponsor) => sponsor.email === user.email
                        ));

                    // Generate display name for sponsors with student initials
                    let displayName = user.name;
                    if (isSponsor) {
                      // Find the student(s) associated with this sponsor
                      const associatedStudents = selectedUsers.filter(
                        (u) => u.role === 'student'
                      );
                      if (associatedStudents.length > 0) {
                        // Get initials from the first student (or combine if multiple)
                        const studentInitials = associatedStudents
                          .map((student) => {
                            const nameParts = student.name.trim().split(' ');
                            const firstInitial = nameParts[0]
                              ? nameParts[0][0].toUpperCase()
                              : '';
                            const lastInitial = nameParts[nameParts.length - 1]
                              ? nameParts[nameParts.length - 1][0].toUpperCase()
                              : '';
                            return firstInitial + lastInitial;
                          })
                          .join('');
                        displayName = `${studentInitials} Sponsor`;
                      } else {
                        displayName = `${user.name} (Sponsor)`;
                      }
                    }

                    return (
                      <Chip
                        key={user.id}
                        label={displayName}
                        color={getRoleChipColor(user.role) as any}
                        variant="outlined"
                        onDelete={() => removeUser(user.id)}
                        deleteIcon={<Close />}
                        size="small"
                      />
                    );
                  })
                )}
              </Paper>
            </Box>

            {/* Role Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select by Role:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {(['student', 'judge', 'volunteer'] as const).map((role) => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Checkbox
                        checked={selectedRoles.has(role)}
                        onChange={(e) =>
                          handleRoleChange(role, e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {role}
                        </Typography>
                        <Box sx={getRoleCountStyle(role)}>
                          {roleCounts[role]}
                        </Box>
                      </Box>
                    }
                  />
                ))}
              </Box>
            </Box>

            {/* Individual User Selection Toggle */}
            <Box sx={{ mb: 3 }}>
              <MuiButton
                variant="outlined"
                onClick={() => setShowUserSearch(!showUserSearch)}
                startIcon={<PersonAdd />}
                endIcon={showUserSearch ? <ExpandLess /> : <ExpandMore />}
                sx={{ mb: 1 }}
              >
                {showUserSearch ? 'Hide' : 'Add'} Individual Users
              </MuiButton>

              <Collapse in={showUserSearch}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Search sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {filteredUsers.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        {searchQuery
                          ? 'No users found matching your search'
                          : 'All users are already added as recipients'}
                      </Typography>
                    ) : (
                      filteredUsers.map((user) => (
                        <Paper
                          key={user.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            mb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <Typography variant="body2">
                                {user.name}
                              </Typography>
                              <Chip
                                label={user.role}
                                color={getRoleChipColor(user.role) as any}
                                size="small"
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {user.email}
                            </Typography>
                          </Box>
                          <MuiButton
                            size="small"
                            variant="outlined"
                            disabled={addingUserId === user.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              addUser(user);
                            }}
                          >
                            {addingUserId === user.id ? 'Adding...' : 'Add'}
                          </MuiButton>
                        </Paper>
                      ))
                    )}
                  </Box>
                </Paper>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Subject */}
            <TextField
              fullWidth
              label="Subject"
              placeholder="Enter email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Message Body */}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Message"
              placeholder="Enter your email message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />

            {/* Recipient Count */}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {selectedUsers.length} recipient
                {selectedUsers.length !== 1 ? 's' : ''} selected
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MuiButton onClick={handleClose}>Cancel</MuiButton>
        <MuiButton
          onClick={handleSendEmail}
          variant="contained"
          startIcon={<Mail />}
          disabled={selectedUsers.length === 0 || !body.trim() || loading}
        >
          {loading ? 'Sending...' : 'Send Email'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}
